import { boardOf, can, draftHiddenFor, goToBoardOf, isMyBoardSlug, ownBoardOnly, type Board, type BoardItem, type BoardsList, type PinView } from "@boem/core/shared";
import { readViewParam, writeViewParam, type SavedViewView, type ViewSpec, DEFAULT_SPEC } from "@boem/core/views";
import { SaveViewModal, SidebarViews } from "./components/Views.tsx";
import { filtersOf, openViewOf, viewsApi } from "./views.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, READ_ONLY_EVENT, type Me } from "./api.ts";
import { BoardsContext, boardsApi, favicon, markSeen, tabTitle, type Boards } from "./boards.ts";
import { apiBase, currentOrg, projectFromSearch, readOrgSetting, withProject, writeOrgSetting } from "./org.ts";
import { SidebarBoards } from "./components/Boards.tsx";
import { PeopleModal } from "./components/People.tsx";
import { SettingsModal, type SettingsNotice } from "./components/GitHub.tsx";
import { lifecycleApi } from "./components/Lifecycle.tsx";
import { NewItemModal } from "./components/ItemModals.tsx";
import { LogView, MenuButton } from "./components/LogView.tsx";
import { NotesContext, useNotesView } from "./components/Notes.tsx";
import { BoardDayContext, useBoardToday } from "./components/Due.tsx";
import { isOAuthPage, OAuthPage } from "./components/OAuthPages.tsx";
import { bridgeLabel, spineLabel, trail, type Crumb } from "./components/PanelHead.tsx";
import { Columns, type ColumnPanel, type ColumnsHandle, type Reveal } from "./components/Panels.tsx";
import { SignIn } from "./components/SignIn.tsx";
import { ProjectSidebar } from "./components/Structure.tsx";
import { GoToContext, isMac, QuickGoTo } from "./components/QuickGoTo.tsx";
import { goToWhere, isGoToShortcut, shortcutOpens, type FindContext } from "./quickFind.ts";
import { WhatsNew } from "./components/WhatsNew.tsx";
import type { Filters } from "./filters.ts";
import * as cascadeOf from "./panels.ts";
import { EMPTY, fromTargets, railed, readPanels, writePanels, type Cascade, type PanelTarget } from "./panels.ts";
import { isNewsUrl, readNews, savedPeriod, savePeriod, urlHasPeriod, writeNews, type NewsView } from "./whatsnew.ts";
import { attachUrl, redeemUploadLink, uploadToken, withoutParam } from "./uploadLink.ts";

/** the board, the archive, or What's new (which is in the URL: ?view=whatsnew). The
 *  flowcharts stay in the artifact: they aren't something the team works on together. */
type View = "log" | "archive" | "whatsnew";

/** The view last open on this board (settings are kept per board: org.ts). */
function savedView(): View {
  return readOrgSetting("buyoem.view") === "archive" ? "archive" : "log";
}

/** The project board last open on this board (its number), or null for the main board. */
function savedProject(): number | null {
  const n = Number(readOrgSetting("buyoem.project"));
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Shown while the board is read-only (BOARD_READ_ONLY, a deploy step): reading works, changes wait. */
function ReadOnlyBanner({ until }: { until: string }) {
  const at = new Date(until).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return (
    <div className="ro-banner" role="status">
      <b>The board is being updated.</b> You can read everything; changes are paused until about {at}.
    </div>
  );
}

/** The top-level card an element is in: the one followed by its slot (LogView marks them), as its number. */
function hostCard(el: Element | null): number | null {
  for (let n = el; n; n = n.parentElement) {
    const slot = n.nextElementSibling;
    if (slot instanceof HTMLElement && slot.dataset.card) return Number(slot.dataset.card);
  }
  return null;
}

/** The viewport's width, and whether it is a phone's (panels are sheets there: app.css's 640px). */
function useViewport() {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return { width: w, phone: w <= 640 };
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [archive, setArchive] = useState<Board | null>(null);
  const [view, setView] = useState<View>(() => (isNewsUrl(window.location.search) ? "whatsnew" : savedView()));
  // what What's new shows (its period, grouping, project and assignee filter), as in its URL
  const [news, setNews] = useState<NewsView>(() => readNews(window.location.search));
  // the items What's new lists: a panel opened from one of its rows points back at it
  const newsNums = useRef<Set<number>>(new Set());
  const onNewsShown = useCallback((nums: Set<number>) => (newsNums.current = nums), []);
  // a project's board instead of the main one (with the Board view)
  const [projectNum, setProjectNum] = useState<number | null>(savedProject);
  // an item to show in a column (0: the board), e.g. after switching boards, or a panel's "Scroll to it"
  const [jumpTo, setJumpTo] = useState<{ column: number; num: number } | null>(null);
  // the boards and projects list as a drawer (under 900px)
  const [drawer, setDrawer] = useState(false);
  const [newProject, setNewProject] = useState(false);
  // the cascading panels beside the board, and the person's pins (saved on the server)
  const [cascade, setCascade] = useState<Cascade>(EMPTY);
  const [pins, setPins] = useState<PinView[] | null>(null);
  // Reset (and Open as board) start the board afresh: filters cleared, or a panel's carried over
  const [boardSeq, setBoardSeq] = useState(0);
  const [boardInit, setBoardInit] = useState<Partial<Filters> | undefined>(undefined);
  // saved views (OEM-268): the board's shared views and mine; the one open on the board (?view=<id>, ?view=quick:verify)
  const [views, setViews] = useState<SavedViewView[] | null>(null);
  const [viewId, setViewId] = useState<string | null>(() => readViewParam(window.location.search));
  // the Save view dialog: a new view (from the board's filters, or Save as new from a view), or a rename
  const [saveView, setSaveView] = useState<{ spec: ViewSpec; from?: string } | { rename: SavedViewView } | null>(null);
  // the main board's filters now: what "+ Save current view" saves
  const boardSpec = useRef<ViewSpec>(DEFAULT_SPEC);
  const onBoardSpec = useCallback((s: ViewSpec) => (boardSpec.current = s), []);
  const nav = useRef<"push" | "replace" | null>(null);
  const reveal = useRef<Reveal | null>(null);
  const columns = useRef<ColumnsHandle>(null);
  // what was clicked last: a panel opened from a reference inside a card is tied to that card
  const lastClick = useRef<Element | null>(null);
  useEffect(() => {
    const on = (e: MouseEvent) => (lastClick.current = e.target instanceof Element ? e.target : null);
    document.addEventListener("click", on, { capture: true });
    return () => document.removeEventListener("click", on, { capture: true });
  }, []);
  const { width, phone } = useViewport();
  // notes on every card (the toolbar toggle), remembered per person on this device
  const notesView = useNotesView(board?.viewer.id ?? null);
  // today on the board, in its time zone (due dates, OEM-267)
  const timeZone = board?.timeZone ?? "America/New_York";
  const today = useBoardToday(timeZone);
  const boardDay = useMemo(() => ({ today, timeZone }), [today, timeZone]);
  const viewRef = useRef(view);
  viewRef.current = view;
  // a panel shows an archived item: keep the archive loaded too
  const needArchive = useRef(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  // the ?org= board doesn't exist, or this person isn't on it (the server says the same either way)
  const [noBoard, setNoBoard] = useState<string | false>(false);
  // ?org=me (or personal): which board that is gets worked out first, then the URL says its slug
  const [orgReady, setOrgReady] = useState(() => !isMyBoardSlug(currentOrg()));
  // the board switcher's list (the boards this person can open), loaded once and when it opens
  const [boardsList, setBoardsList] = useState<BoardsList | null>(null);
  // ?org=me found my board closed: it can be restored
  const [restorable, setRestorable] = useState(false);
  // the People screen of a personal board, which the URL says too (?view=people)
  const [peopleOpen, setPeopleOpen] = useState(() => new URLSearchParams(window.location.search).get("view") === "people");
  // the board's settings (OEMUAP admins: GitHub), which the URL says too (?view=settings); GitHub sends admins back to it
  const [settingsOpen, setSettingsOpen] = useState(() => new URLSearchParams(window.location.search).get("view") === "settings");
  const [settingsNotice] = useState<SettingsNotice | null>(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("view") !== "settings") return null;
    return { connected: q.get("connected"), installed: q.get("installed") === "1", error: q.get("error") };
  });
  // the board is being updated (BOARD_READ_ONLY): until when changes are paused, from /api/me or a refused change
  const [readOnlyUntil, setReadOnlyUntil] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // the quick go-to (Ctrl+/, OEM-213): open with the project in focus when it opened; the focus to give back
  const [goTo, setGoTo] = useState<FindContext | null>(null);
  const goToFocus = useRef<HTMLElement | null>(null);
  const openGoToRef = useRef<() => void>(() => {});
  const mac = useMemo(isMac, []);

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 4000);
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const m = await api.me();
      setReadOnlyUntil(m.readOnly?.until ?? null);
      setMe(m);
      setOffline(false);
    } catch {
      setOffline(true);
    }
  }, []);

  // the board always; the archive too while it's the view on screen (or a panel shows something in it)
  const refresh = useCallback(async () => {
    try {
      // (What's new lists verified items, which are in the archive)
      const [b, a, v] = await Promise.all([
        api.board(),
        viewRef.current === "archive" || viewRef.current === "whatsnew" || needArchive.current ? api.archive() : null,
        // the saved views (their counts are worked out from the board)
        viewsApi.list().catch(() => null),
      ]);
      setBoard(b);
      if (a) setArchive(a);
      if (v) setViews(v);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setBoard(null);
        await loadMe();
      } else if (e instanceof ApiError && e.status === 404 && e.code === "not_found") {
        setNoBoard("This board is private, or it doesn't exist.");
      } else throw e;
    }
  }, [loadMe]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // an upload link from Claude (/?upload=<token>): redeemed once, for this person, then its board
  // opens with Add screenshots on the item or note (?attach=, which LogView reads)
  const redeeming = useRef(false);
  useEffect(() => {
    const token = uploadToken(window.location.search);
    if (me?.state !== "member" || !token || redeeming.current) return;
    redeeming.current = true;
    redeemUploadLink(token).then(
      (r) => window.location.replace(attachUrl(r)),
      (e) => {
        window.history.replaceState(window.history.state, "", window.location.pathname + withoutParam(window.location.search, "upload") + window.location.hash);
        toast(e instanceof ApiError ? e.message : "That upload link didn't work. Ask Claude for a new one.");
      },
    );
  }, [me, toast]);

  // a change refused because the board is read-only shows the banner; it goes when the window ends
  useEffect(() => {
    const on = (e: Event) => setReadOnlyUntil((e as CustomEvent<string | null>).detail ?? new Date(Date.now() + 60_000).toISOString());
    window.addEventListener(READ_ONLY_EVENT, on);
    return () => window.removeEventListener(READ_ONLY_EVENT, on);
  }, []);
  useEffect(() => {
    if (!readOnlyUntil) return;
    const t = setTimeout(() => setReadOnlyUntil(null), Math.max(0, new Date(readOnlyUntil).getTime() - Date.now()) + 1000);
    return () => clearTimeout(t);
  }, [readOnlyUntil]);

  const loadBoards = useCallback(() => {
    boardsApi.list().then(setBoardsList, () => {});
  }, []);

  // ?org=me: my own board, made the first time I open it; then the URL names it (?org=gautam)
  useEffect(() => {
    if (me?.state !== "member" || orgReady) return;
    (async () => {
      const list = await boardsApi.list();
      setBoardsList(list);
      const mine = list.boards.find((b) => b.mine && b.exists);
      const slug = mine?.slug ?? (await boardsApi.openMine()).slug;
      const q = new URLSearchParams(window.location.search);
      q.set("org", slug);
      window.history.replaceState(window.history.state, "", `${window.location.pathname}?${q.toString()}${window.location.hash}`);
      setOrgReady(true);
    })().catch((e) => {
      // my own board, closed: offer to bring it back rather than making a second one (orgs.md 1.5)
      if (e instanceof ApiError && e.code === "board_closed") setRestorable(true);
      setNoBoard(e instanceof ApiError && e.code !== "not_found" ? e.message : "This board is private, or it doesn't exist.");
    });
  }, [me, orgReady]);

  useEffect(() => {
    if (me?.state !== "member" || !orgReady) return;
    refresh().catch(() => setOffline(true));
    loadBoards();
    // pinned panels open again at sign-in
    api.pins().then(setPins, () => setPins([]));
  }, [me, refresh, orgReady, loadBoards]);

  // live updates: any change anywhere (web, Claude, another tab) refreshes the board
  useEffect(() => {
    if (me?.state !== "member" || !orgReady) return;
    let t: ReturnType<typeof setTimeout> | undefined;
    // this board's changes only (the server filters by org)
    const es = new EventSource(`${apiBase()}/stream`);
    es.addEventListener("change", () => {
      clearTimeout(t);
      t = setTimeout(() => refresh().catch(() => {}), 150);
    });
    // (re)connected: anything that changed while the connection was down was missed, so reload the board
    es.onopen = () => {
      setOffline(false);
      refresh().catch(() => {});
    };
    // the server closed this board's stream for good (I left it or was removed, orgs.md 5): the
    // reconnect is refused, and the board itself answers "private, or doesn't exist"
    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) refresh().catch(() => {});
    };
    return () => {
      clearTimeout(t);
      es.close();
    };
  }, [me, refresh, orgReady]);

  // which board this tab is: its name in the tab's title, a dot on its icon, the personal look (orgs.css)
  const orgKind = board?.org.kind;
  const orgName = board?.org.name;
  useEffect(() => {
    if (!orgKind || !orgName) return;
    document.body.dataset.org = orgKind;
    document.title = tabTitle({ name: orgName }, view === "archive" ? "Archive" : view === "whatsnew" ? "What's new" : "Board");
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favicon(orgKind === "personal");
  }, [orgKind, orgName, view]);
  // a board shared with me loses its dot once I open it
  const orgSlug = board?.org.slug;
  useEffect(() => {
    if (orgKind === "personal" && orgSlug) markSeen(orgSlug);
  }, [orgKind, orgSlug]);

  // opening the archive (or What's new, whose verified items are in it) loads it
  useEffect(() => {
    if ((view === "archive" || view === "whatsnew") && me?.state === "member") api.archive().then(setArchive, () => setOffline(true));
  }, [view, me]);

  // a What's new link without a period: the one this person picked last
  const viewerId = board?.viewer.id ?? null;
  const viewerRef = useRef(viewerId);
  viewerRef.current = viewerId;
  useEffect(() => {
    if (!viewerId || viewRef.current !== "whatsnew" || urlHasPeriod(window.location.search)) return;
    const v = { ...readNews(window.location.search), period: savedPeriod(viewerId) };
    setNews(v);
    window.history.replaceState(window.history.state, "", window.location.pathname + writeNews(window.location.search, v) + window.location.hash);
  }, [viewerId]);

  // Back and Forward in and out of a view
  useEffect(() => {
    const onPop = () => setViewId(readViewParam(window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // a ?view= link to a view this person's list doesn't have: ask the server why (another's personal view, or gone)
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!viewId || !views || openViewOf(viewId, views) || asked.current === viewId) return;
    asked.current = viewId;
    viewsApi.get(viewId).then(
      (v) => setViews((cur) => (cur ? [...cur, v] : [v])),
      (e) => {
        toast(e instanceof ApiError && e.status === 403 ? e.message : "That view isn't on this board: it was deleted, or the link is from another board.");
        setViewId(null);
        const url = window.location.pathname + writeViewParam(window.location.search, null) + window.location.hash;
        window.history.replaceState(window.history.state, "", url);
      },
    );
  }, [viewId, views]);

  // Escape closes the drawer
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setDrawer(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer]);

  // Ctrl+/ (Cmd+/): the quick go-to. Typing in another field, it waits for the same keys again; pressed
  // in the go-to's own box, it closes it
  const goToOpen = goTo != null;
  const lastInField = useRef<number | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isGoToShortcut(e)) return;
      if (goToOpen) {
        e.preventDefault();
        setGoTo(null);
        goToFocus.current?.focus({ preventScroll: true });
        return;
      }
      const t = e.target as HTMLElement | null;
      const typing = !!t?.closest?.("input, textarea, select, [contenteditable=''], [contenteditable='true']");
      const now = Date.now();
      if (!shortcutOpens(typing, now, lastInField.current)) {
        lastInField.current = now;
        return;
      }
      lastInField.current = null;
      // not over another dialog
      if (document.querySelector(".scrim, .sheet-scrim, .lightbox")) return;
      e.preventDefault();
      openGoToRef.current();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [goToOpen]);

  // -- the cascade --------------------------------------------------------------------
  const pinKeys = useMemo(() => new Set((pins ?? []).map((p) => `${p.kind}:${p.num}`)), [pins]);
  const isPinned = useCallback((t: PanelTarget) => pinKeys.has(`${t.kind}:${t.num}`), [pinKeys]);
  /** change the chain; the URL follows (a new history entry, so Back undoes it) */
  const change = (fn: (c: Cascade) => Cascade, how: "push" | "replace" | null = "push") => {
    nav.current = how;
    setCascade(fn);
  };
  useEffect(() => {
    const how = nav.current;
    nav.current = null;
    if (!how) return;
    const url = window.location.pathname + writePanels(window.location.search, cascade.panels) + window.location.hash;
    if (url === window.location.pathname + window.location.search + window.location.hash) return;
    window.history[how === "push" ? "pushState" : "replaceState"](window.history.state, "", url);
  }, [cascade]);
  // Back and Forward walk the chain (and in and out of What's new)
  useEffect(() => {
    const onPop = () => {
      setCascade((c) => fromTargets(readPanels(window.location.search), c));
      if (isNewsUrl(window.location.search)) {
        setView("whatsnew");
        setNews(readNews(window.location.search, savedPeriod(viewerRef.current)));
      } else setView((v) => (v === "whatsnew" ? savedView() : v));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // once the board is in: a ?panels= link opens that chain; else the pinned panels open, in pin order
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !board || pins == null) return;
    started.current = true;
    const linked = readPanels(window.location.search);
    const c = linked.length ? fromTargets(linked) : fromTargets(pins, EMPTY, false);
    if (!c.panels.length) return;
    reveal.current = { all: true };
    change(() => c, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, pins]);
  // Escape closes the newest panel (after a dialog, a menu or the drawer)
  const panelCount = cascade.panels.length;
  useEffect(() => {
    if (!panelCount) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if ((e.target as HTMLElement | null)?.closest?.("input, textarea, select")) return;
      if (drawer || document.querySelector(".scrim, .lightbox, .fpop, .menu")) return;
      change(cascadeOf.back);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [panelCount, drawer]);

  /** the URL with What's new in it (or taken out); a new history entry, so Back returns */
  const newsUrl = (v: NewsView | null, how: "push" | "replace") => {
    const url = window.location.pathname + writeNews(window.location.search, v) + window.location.hash;
    if (url !== window.location.pathname + window.location.search + window.location.hash) window.history[how === "push" ? "pushState" : "replaceState"](window.history.state, "", url);
  };
  /** the view param in the URL (null: taken out); a new history entry when `push` */
  const viewUrl = (id: string | null, push: boolean) => {
    const url = window.location.pathname + writeViewParam(withProject(window.location.search, null), id) + window.location.hash;
    if (url !== window.location.pathname + window.location.search + window.location.hash) window.history[push ? "pushState" : "replaceState"](window.history.state, "", url);
  };
  const show = (v: View) => {
    setDrawer(false);
    // the archive and What's new aren't a view
    if (v !== "log" && viewId) {
      setViewId(null);
      if (v === "archive") viewUrl(null, false);
    }
    if (v === "whatsnew") {
      // it opens with the period this person picked last
      const next = view === "whatsnew" ? news : { ...news, period: savedPeriod(viewerId) };
      setNews(next);
      newsUrl(next, "push");
    } else if (view === "whatsnew") newsUrl(null, "push");
    setView(v);
    // What's new is in the URL instead
    if (v === "whatsnew") return;
    writeOrgSetting("buyoem.view", v);
  };
  /** What's new changed what it shows: the URL follows (no new history entry); a picked period is remembered */
  const changeNews = (v: NewsView, period?: boolean) => {
    setNews(v);
    if (period) savePeriod(viewerId, v.period);
    newsUrl(v, "replace");
  };

  /** the board column back at its top (the page while no panel is docked, else the column) */
  const boardTop = () => {
    window.scrollTo({ top: 0 });
    document.querySelector<HTMLElement>('.cols[data-docked] > [data-col="0"]')?.scrollTo({ top: 0 });
  };

  const openBoard = (num: number | null, jump?: number) => {
    setProjectNum(num);
    setBoardInit(undefined);
    // a board, not a view
    if (viewId) {
      setViewId(null);
      viewUrl(null, false);
    }
    writeOrgSetting("buyoem.project", num == null ? null : String(num));
    // the project board is in the URL too (&project=01), so a link opens it and two tabs can differ
    const no = num == null ? null : (board?.items.find((i) => i.num === num)?.projectNo ?? null);
    const url = window.location.pathname + withProject(window.location.search, no) + window.location.hash;
    if (url !== window.location.pathname + window.location.search + window.location.hash) window.history.replaceState(window.history.state, "", url);
    show("log");
    if (jump != null) setJumpTo({ column: 0, num: jump });
    // another board opens at its top (a jump scrolls to its item instead)
    else boardTop();
  };

  // every project, by its number, for the sidebar; and the main board's open items
  const projects = useMemo(
    () =>
      (board?.items ?? [])
        .filter((i) => i.kind === "project")
        .sort((a, b) => (a.projectNo ?? Infinity) - (b.projectNo ?? Infinity) || a.num - b.num),
    [board],
  );
  // (someone else's draft isn't counted: it's off their board until it's ready, OEM-286)
  const mainOpen = useMemo(() => (board?.items ?? []).filter((i) => i.kind !== "project" && i.status === "open" && !ownBoardOnly(i) && !draftHiddenFor(i, board?.viewer.id)).length, [board]);
  const byNum = useMemo(() => {
    const m = new Map<number, BoardItem>();
    for (const i of archive?.items ?? []) m.set(i.num, i);
    for (const i of board?.items ?? []) m.set(i.num, i);
    return m;
  }, [board, archive]);
  // what the quick go-to searches: this board and its archive (nothing from another board)
  const goToItems = useMemo(() => [...byNum.values()], [byNum]);
  const parentOf = useCallback((n: number) => byNum.get(n)?.parent?.num ?? null, [byNum]);
  const pinnedProjects = useMemo(() => new Set((pins ?? []).filter((p) => p.kind === "project").map((p) => p.num)), [pins]);

  // a panel showing something that's only in the archive: load the archive as well
  const missing = cascade.panels.some((p) => board && !board.items.some((i) => i.num === p.num));
  needArchive.current = missing;
  useEffect(() => {
    if (missing && !archive && me?.state === "member") api.archive().then(setArchive, () => {});
  }, [missing, archive, me]);

  const signOut = async () => {
    await api.signOut();
    setBoard(null);
    setPins(null);
    setCascade(EMPTY);
    started.current = false;
    window.history.replaceState(null, "", "/");
    await loadMe();
  };

  if (!me) {
    return <div className="empty">{offline ? "Can't reach the server." : "Loading…"}</div>;
  }
  // connecting Claude: the OAuth server's sign-in and consent pages
  if (isOAuthPage()) return <OAuthPage me={me} />;
  if (me.state !== "member") return <SignIn me={me} onSignedIn={loadMe} />;

  if (noBoard)
    return (
      <div className="empty">
        {noBoard}{" "}
        {restorable && (
          <button
            type="button"
            className="btn primary"
            onClick={() =>
              lifecycleApi.restoreMine().then(
                (r) => (window.location.href = `/?org=${encodeURIComponent(r.slug)}`),
                (e) => setNoBoard(e instanceof ApiError ? e.message : "Couldn't reach the server."),
              )
            }
          >
            Restore my board
          </button>
        )}{" "}
        <a href="/">Open OEMUAP</a>
      </div>
    );
  if (!board) return <div className="empty">{offline ? "Can't reach the server." : "Loading the board…"}</div>;

  // the URL's &project=01 wins over the one last open on this board
  const urlProject = projectFromSearch(window.location.search);
  const urlProjectNum = urlProject != null ? (board.items.find((i) => i.kind === "project" && i.projectNo === urlProject)?.num ?? null) : null;
  const openProject = (urlProjectNum ?? projectNum) != null ? (board.items.find((i) => i.num === (urlProjectNum ?? projectNum) && i.kind === "project") ?? null) : null;
  /** the People screen opens and closes with ?view=people in the URL */
  const people = (open: boolean) => {
    setPeopleOpen(open);
    const q = new URLSearchParams(window.location.search);
    if (open) q.set("view", "people");
    else if (viewId) q.set("view", viewId);
    else q.delete("view");
    const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${s ? `?${s}` : ""}${window.location.hash}`);
  };
  /** the settings open and close with ?view=settings in the URL (GitHub's notes, ?connected= ..., go once it closes) */
  const settings = (open: boolean) => {
    setSettingsOpen(open);
    const q = new URLSearchParams(window.location.search);
    for (const k of ["tab", "connected", "installed", "error"]) q.delete(k);
    if (open) q.set("view", "settings");
    else if (viewId) q.set("view", viewId);
    else q.delete("view");
    const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${s ? `?${s}` : ""}${window.location.hash}`);
  };
  const personalBoard = board.org?.kind === "personal";
  // Settings (the main board's cogwheel, the switcher's entry): admins, and leads for the GitHub tab's repos
  const mayConfigure = !personalBoard && can(board.viewer, "settings");
  const boards: Boards = {
    org: board.org,
    list: boardsList,
    reload: loadBoards,
    viewerId: board.viewer.id,
    openPeople: personalBoard ? () => people(true) : undefined,
    openSettings: mayConfigure ? () => settings(true) : undefined,
    openMain: () => {
      setDrawer(false);
      openBoard(null);
    },
  };
  const menu = { onMenu: () => setDrawer((d) => !d), menuOpen: drawer };

  // -- saved views (OEM-268) ----------------------------------------------------------
  const currentView = view === "log" && viewId ? openViewOf(viewId, views) : null;
  const reloadViews = () => viewsApi.list().then(setViews, () => {});
  /** open a view: the main board's list across every project, with the view's filters */
  const openView = (id: string) => {
    setDrawer(false);
    setProjectNum(null);
    writeOrgSetting("buyoem.project", null);
    setBoardInit(undefined);
    setViewId(id);
    viewUrl(id, true);
    setView("log");
    writeOrgSetting("buyoem.view", "log");
    setBoardSeq((n) => n + 1);
    boardTop();
  };
  const viewFail = (e: unknown) => toast(e instanceof ApiError ? e.message : "Couldn't reach the server.");
  const viewActions = {
    onRename: (v: SavedViewView) => setSaveView({ rename: v }),
    onEditFilters: (v: SavedViewView) => {
      openView(v.id);
      if (v.canEdit) toast(`Change the filters of "${v.name}", then Save changes.`);
    },
    onShare: (v: SavedViewView, shared: boolean) =>
      viewsApi.update(v.id, { shared }).then(() => {
        reloadViews();
        toast(shared ? `"${v.name}" is shared with the board.` : `"${v.name}" is yours only now.`);
      }, viewFail),
    onMove: (v: SavedViewView, by: -1 | 1) => {
      const same = (views ?? []).filter((x) => x.shared === v.shared).map((x) => x.id);
      const i = same.indexOf(v.id);
      const j = i + by;
      if (i < 0 || j < 0 || j >= same.length) return;
      [same[i], same[j]] = [same[j]!, same[i]!];
      viewsApi.reorder(v.shared ? "shared" : "mine", same).then(setViews, viewFail);
    },
    onDelete: (v: SavedViewView) => {
      if (!window.confirm(`Delete the view "${v.name}"?${v.shared ? " It goes from everyone's sidebar." : ""} The items stay as they are.`)) return;
      viewsApi.remove(v.id).then(() => {
        reloadViews();
        if (viewId === v.id) openBoard(null);
        toast(`Deleted the view "${v.name}".`);
      }, viewFail);
    },
  };
  const onSaveView = (spec: ViewSpec, how: "changes" | "new") => {
    if (how === "new" || !currentView?.saved) return setSaveView({ spec, from: currentView?.name });
    viewsApi.update(currentView.saved.id, { filters: spec }).then((v) => {
      setViews((cur) => (cur ?? []).map((x) => (x.id === v.id ? v : x)));
      toast(`Saved the changes to "${v.name}".`);
    }, viewFail);
  };
  const mayShareViews = can(board.viewer, "view_shared");
  const panels = cascade.panels;

  /** Open something in a panel right of `column`, keeping the card it came from where it is on screen. */
  const openPanel = (column: number, target: PanelTarget, from: { num: number; via?: number } | null) => {
    // a reference in a card's text or place line: the panel came from that card
    const via = from && from.via == null ? hostCard(lastClick.current) : null;
    const source = from && via != null && via !== from.num ? { ...from, via } : from;
    const row = document.querySelector<HTMLElement>(".cols");
    const card = source && row ? columnsLocate(row, column, source.num, source.via) : null;
    // (a notes panel is tied to the card's notes row, so that is what stays in view)
    reveal.current = source ? { column, num: source.num, via: source.via, top: card ? card.getBoundingClientRect().top : null, ...(target.kind === "notes" ? { part: "notes" as const } : {}) } : null;
    setDrawer(false);
    change((c) => cascadeOf.open(c, column, target, source, isPinned));
  };
  const columnsLocate = (row: HTMLElement, column: number, num: number, via?: number) => {
    const slot = (n: number) => row.querySelector(`:scope > [data-col="${column}"] [data-card="${n}"]`)?.previousElementSibling ?? null;
    return slot(via ?? num) ?? slot(num);
  };

  /** Is item `num` in what column `column` shows (so the "From" line can point at it)? */
  const shows = (column: number, num: number): boolean => {
    const it = byNum.get(num);
    if (!it) return false;
    if (column === 0) {
      if (view === "whatsnew") return newsNums.current.has(num);
      if (view === "archive") return !!archive?.items.some((i) => i.num === num);
      return openProject ? it.num === openProject.num || it.project?.num === openProject.num : !ownBoardOnly(it) && !it.archived;
    }
    const p = panels[column - 1];
    if (!p) return false;
    // a notes panel shows one item's notes: a reference in them came from that item
    if (p.kind === "notes") return num === p.num;
    if (byNum.get(p.num)?.kind === "project") return it.project?.num === p.num;
    // an item's panel: the item, what's under it, and what its place line names (its parent, its project)
    const up = (from: number, to: number) => {
      for (let n: number | null = from, i = 0; n != null && i < 5; n = byNum.get(n)?.parent?.num ?? null, i++) if (n === to) return true;
      return false;
    };
    return up(num, p.num) || up(p.num, num);
  };

  const resetAll = () => {
    change((c) => cascadeOf.reset(c, isPinned, (s) => {
      const it = byNum.get(s.via ?? s.num);
      return !!it && !ownBoardOnly(it) && !it.archived;
    }));
    openBoard(null);
    setBoardSeq((n) => n + 1);
  };

  const togglePin = (target: PanelTarget) => {
    const on = isPinned(target);
    (on ? api.unpin(target.num) : api.pin(target.num, target.kind)).then(
      (r) => {
        setPins(r);
        toast(on ? "Unpinned: it closes on Reset like the others." : "Pinned: it stays through Reset and opens again when you sign in, on any device.");
      },
      (e) => toast(e instanceof ApiError ? e.message : "Couldn't reach the server."),
    );
  };

  const boardLabel = view === "archive" ? "Archive" : view === "whatsnew" ? "What's new" : openProject ? spineLabel(openProject, openProject.num) : "Board";
  const labelsFor = (upTo: number) =>
    trail(
      boardLabel,
      panels.slice(0, upTo + 1).map((p) => {
        const it = byNum.get(p.num) ?? null;
        return { kind: p.kind === "notes" ? ("notes" as const) : it?.kind === "project" ? ("project" as const) : it ? ("item" as const) : p.kind, item: it, num: p.num };
      }),
      view === "log" ? (openProject?.num ?? null) : null,
    );

  const columnPanels: ColumnPanel[] = panels.map((p, i) => {
    const it = byNum.get(p.num) ?? null;
    const kind = p.kind === "notes" ? "notes" : it?.kind === "project" ? "project" : it ? "item" : p.kind;
    const pinned = isPinned(p);
    const s = p.source;
    // the card it came from: the one a clicked row or reference is on (an item's panel has just the one)
    const holder = s && s.column > 0 ? panels[s.column - 1] : null;
    const fromNum = !s ? null : (s.via ?? (holder && byNum.get(holder.num)?.kind !== "project" && s.num !== holder.num ? holder.num : s.num));
    const srcItem = s && fromNum != null && shows(s.column, fromNum) ? (byNum.get(fromNum) ?? null) : null;
    const labels = labelsFor(i);
    const crumbs: Crumb[] = labels.map((label, j) => (j === labels.length - 1 ? { label } : { label, onClick: () => change((c) => cascadeOf.close(c, j)) }));
    // an item only in the archive: its panel's view has it alongside the board's items
    const panelBoard = it && !board.items.some((x) => x.num === it.num) ? { ...board, items: [...board.items, it] } : board;
    return {
      id: p.id,
      kind,
      // (a notes panel's trail step already says "Notes 03-01")
      label: kind === "notes" ? labels[labels.length - 1]! : `${kind === "project" ? "Project" : "Item"} ${labels[labels.length - 1]}`,
      spine: kind === "notes" ? `Notes ${spineLabel(it, p.num)}` : spineLabel(it, p.num),
      pinned,
      source: s && srcItem ? { column: s.column, num: s.num, via: s.via, label: bridgeLabel(srcItem), ...(kind === "notes" && s.num === p.num ? { part: "notes" as const } : {}) } : null,
      content: (
        <LogView
          mode="panel"
          column={i + 1}
          board={panelBoard}
          project={kind === "project" ? it : null}
          panel={{
            kind,
            num: p.num,
            pinned,
            from: srcItem ? { label: bridgeLabel(srcItem), title: srcItem.title } : null,
            crumbs,
            onBack: () => change(cascadeOf.back),
            onClose: () => change((c) => cascadeOf.close(c, i)),
            onPin: () => togglePin({ kind: p.kind, num: p.num }),
            onReset: phone ? resetAll : undefined,
            onScrollToSource: srcItem ? () => columns.current?.showSource(i) : undefined,
            onOpenAsBoard:
              kind === "project"
                ? (f) => {
                    change((c) => cascadeOf.openAsBoard(c, i, isPinned));
                    openBoard(p.num);
                    setBoardInit({ lanes: new Set(f.lanes), q: f.q, kind: f.kind, asg: [...f.asg], high: f.high, notReviewed: f.notReviewed, sort: f.sort });
                    setBoardSeq((n) => n + 1);
                    const queues = [...f.lanes].map((l) => ({ todo: "To do", review: "PR review", deploy: "Deploy", verify: "Verify", closed: "Closed" })[l]);
                    toast(`${spineLabel(it, p.num)} is the board now; its filters came along (${[...queues, ...(f.q ? [`"${f.q}"`] : [])].join(", ") || "none"}).`);
                  }
                : undefined,
            onGoTo:
              (kind === "item" || kind === "notes") && it
                ? () => {
                    // on a phone the board is under the sheets: put them away (pinned ones stay)
                    if (phone) change((c) => cascadeOf.reset(c, isPinned));
                    // its project's board when it's in one (as ↗ on a sub-item row), else the main board
                    openBoard(goToBoardOf(it), it.num);
                  }
                : undefined,
          }}
          onPanel={(t, src) => openPanel(i + 1, t, src)}
          onSwitch={openBoard}
          refresh={refresh}
          onSignOut={signOut}
          toast={toast}
          jumpTo={jumpTo?.column === i + 1 ? jumpTo.num : null}
          onJumped={() => setJumpTo(null)}
          offline={offline}
        />
      ),
    };
  });

  const onBoardPanel = (t: PanelTarget, src: { num: number; via?: number }) => openPanel(0, t, src);

  /** The quick go-to opens: the project in focus is a project panel the focus (or the last click) is in, else the project board open. */
  openGoToRef.current = () => {
    const focused = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    goToFocus.current = focused;
    const at = (focused ?? lastClick.current)?.closest?.("[data-col]")?.getAttribute("data-col");
    const p = at && Number(at) > 0 ? panels[Number(at) - 1] : null;
    const inPanel = p && p.kind === "project" && byNum.get(p.num)?.kind === "project" ? p.num : null;
    setGoTo({ project: inPanel ?? (view === "log" ? (openProject?.num ?? null) : null) });
    // archived items are found too (after open and closed ones)
    if (!archive) api.archive().then(setArchive, () => {});
  };
  /** Go to an item from the quick go-to: its card when the board on screen shows it, else a panel beside the board. */
  const goToItem = (it: BoardItem, shift: boolean) => {
    setGoTo(null);
    const col0 = document.querySelector<HTMLElement>('.cols > [data-col="0"]');
    const onBoard = (view === "log" || view === "archive") && !!col0?.querySelector(`[data-card="${it.num}"], #item-${it.num}`);
    if (goToWhere(onBoard, shift) === "scroll") setJumpTo({ column: 0, num: it.num });
    else openPanel(0, { kind: it.kind === "project" ? "project" : "item", num: it.num }, { num: it.num });
  };
  const goToScope = board.org?.name ?? "this board";
  const onReset = panels.length ? resetAll : undefined;
  const boardJump = jumpTo?.column === 0 ? jumpTo.num : null;

  return (
    <BoardsContext.Provider value={boards}>
    <GoToContext.Provider value={{ open: () => openGoToRef.current(), mac }}>
    <NotesContext.Provider value={notesView}>
    <BoardDayContext.Provider value={boardDay}>
      <div className="shell" data-panels={panels.length || undefined} data-rail={!phone && railed(width, panels.length) ? "1" : undefined}>
        <ProjectSidebar
          top={<SidebarBoards />}
          personal={board.org?.kind === "personal"}
          projects={projects}
          active={openProject?.num ?? null}
          view={view}
          mainOpen={mainOpen}
          open={drawer}
          pinned={pinnedProjects}
          onBoard={(num) => openBoard(num)}
          onPanel={(num) => openPanel(0, { kind: "project", num }, { num })}
          onNewProject={
            can(board.viewer, "project") && can(board.viewer, "create")
              ? () => {
                  setDrawer(false);
                  setNewProject(true);
                }
              : undefined
          }
          onArchive={() => {
            show("archive");
            boardTop();
          }}
          onNews={() => {
            show("whatsnew");
            boardTop();
          }}
          onClose={() => setDrawer(false)}
          viewOpen={!!currentView}
          views={
            <SidebarViews
              views={views}
              items={board.items}
              viewer={board.viewer}
              company={board.org?.kind !== "personal"}
              today={today}
              active={currentView?.id ?? null}
              onOpen={openView}
              onSaveCurrent={() => {
                setDrawer(false);
                setSaveView({ spec: boardSpec.current });
              }}
              actions={viewActions}
            />
          }
        />
        <Columns
          handle={columns}
          panels={columnPanels}
          expanded={cascade.expanded}
          phone={phone}
          onExpand={(id) => change((c) => cascadeOf.expand(c, id), null)}
          onMissingSource={(column, num) => setJumpTo({ column, num })}
          parentOf={parentOf}
          reveal={reveal}
          board={
            view === "log" ? (
              <LogView
                key={`board-${currentView ? `view-${currentView.id}` : (openProject?.num ?? "main")}-${boardSeq}`}
                board={board}
                project={currentView ? null : openProject}
                initial={currentView ? filtersOf(currentView.spec) : boardInit}
                view={currentView}
                onSaveView={onSaveView}
                onSpec={onBoardSpec}
                onSwitch={openBoard}
                onPanel={onBoardPanel}
                onReset={onReset}
                refresh={refresh}
                onSignOut={signOut}
                toast={toast}
                jumpTo={boardJump}
                onJumped={() => setJumpTo(null)}
                // not on the board: it may be in the archive
                onMissing={(n) => {
                  setJumpTo({ column: 0, num: n });
                  show("archive");
                }}
                offline={offline}
                {...menu}
              />
            ) : view === "archive" ? (
              archive ? (
                <LogView
                  key="archive"
                  mode="archive"
                  board={archive}
                  onPanel={onBoardPanel}
                  onReset={onReset}
                  refresh={refresh}
                  onSignOut={signOut}
                  toast={toast}
                  jumpTo={boardJump}
                  onJumped={() => setJumpTo(null)}
                  onMissing={(n) => {
                    const it = board.items.find((i) => i.num === n);
                    if (!it) return toast(`#${n} isn't on the board or in the archive.`);
                    openBoard(boardOf(it), n);
                  }}
                  offline={offline}
                  {...menu}
                />
              ) : (
                <div className="empty">Loading the archive&hellip;</div>
              )
            ) : (
              <WhatsNew
                board={board}
                view={news}
                onView={changeNews}
                byNum={(n) => byNum.get(n)}
                onPanel={onBoardPanel}
                onShown={onNewsShown}
                onReset={onReset}
                onMenu={menu.onMenu}
                menuOpen={menu.menuOpen}
                offline={offline}
                toast={toast}
              />
            )
          }
        />
        {saveView && (
          <SaveViewModal
            title={"rename" in saveView ? `Rename "${saveView.rename.name}"` : saveView.from ? `Save as a new view (from "${saveView.from}")` : "Save the board's filters as a view"}
            rename={"rename" in saveView}
            initialName={"rename" in saveView ? saveView.rename.name : ""}
            spec={"spec" in saveView ? saveView.spec : undefined}
            canShare={mayShareViews}
            onClose={() => setSaveView(null)}
            onSave={async (name, shared) => {
              if ("rename" in saveView) {
                const v = await viewsApi.update(saveView.rename.id, { name });
                setViews((cur) => (cur ?? []).map((x) => (x.id === v.id ? v : x)));
                toast(`Renamed it "${v.name}".`);
                return;
              }
              const v = await viewsApi.create({ name, filters: saveView.spec, shared });
              await reloadViews();
              setViews((cur) => (cur?.some((x) => x.id === v.id) ? cur : [...(cur ?? []), v]));
              openView(v.id);
              toast(`Saved the view "${v.name}"${v.shared ? ", shared with the board" : ""}. It's under Views in the sidebar.`);
            }}
          />
        )}
        {newProject && (
          <NewItemModal
            presetKind="project"
            viewer={board.viewer}
            onClose={() => setNewProject(false)}
            onCreated={(n) => {
              // once the board has it, open its own board (the main board lists no project cards)
              refresh()
                .catch(() => {})
                .then(() => openBoard(n));
              toast(`Logged as #${n}.`);
            }}
          />
        )}
        {peopleOpen && personalBoard && (
          <PeopleModal
            onClose={() => people(false)}
            onChanged={() => {
              loadBoards();
              refresh().catch(() => {});
            }}
          />
        )}
        {settingsOpen && mayConfigure && <SettingsModal onClose={() => settings(false)} toast={toast} notice={settingsNotice} viewer={board.viewer} />}
        {goTo && (
          <QuickGoTo
            items={goToItems}
            ctx={goTo}
            scope={goToScope}
            onGo={goToItem}
            onClose={(restore) => {
              setGoTo(null);
              if (restore) goToFocus.current?.focus({ preventScroll: true });
            }}
          />
        )}
        {readOnlyUntil && <ReadOnlyBanner until={readOnlyUntil} />}
        {toastMsg && (
          <div className="toast" data-above={readOnlyUntil ? "1" : undefined} role="status" onClick={() => setToastMsg(null)}>
            {toastMsg}
          </div>
        )}
      </div>
    </BoardDayContext.Provider>
    </NotesContext.Provider>
    </GoToContext.Provider>
    </BoardsContext.Provider>
  );
}
